import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
const baseUrl = process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:5000';
// 🔹 Async thunk to fetch business user dashboard
export const fetchBusinessUserDashboard = createAsyncThunk(
  'businessUser/fetchDashboard',
  async (_, { getState, rejectWithValue }) => {
    try {
      const token = getState().auth.token;
      if (!token) throw new Error('Authorization token is required');

      const response = await axios.get(`${baseUrl}/business/dashboard`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// 🔹 Async thunk to update business user profile
export const updateBusinessUserProfile = createAsyncThunk(
  'businessUser/updateProfile',
  async ({ id, profileData, profilePic }, { getState, rejectWithValue }) => {
    try {
      const token = getState().auth.token;
      if (!token) throw new Error('Authorization token is required');

      const formData = new FormData();

      // Append all fields
      Object.entries(profileData).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, value);
        }
      });

      // Add profilePic if available
      if (profilePic) {
        formData.append('profilePic', profilePic);
      }

      const response = await axios.put(
        `${baseUrl}/business/update/${id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// 🔹 Initial form data state
const initialFormData = {
  fullName: '',
  email: '',
  phone: '',
  username: '',
  description: '',
  profession: '',
  dob: '',
  gender: '',
  village: '',
  city: '',
  district: '',
  state: '',
  country: '',
  postalCode: '',
  googleMapLink: '',
  categoryPath: '',
  profilePic: null,
  profilePicUrl: '', // Add this for displaying the profile picture URL
};

// 🔹 Slice
const businessUserSlice = createSlice({
  name: 'businessUser',
  initialState: {
    profile: null,
    appointments: [],
    formData: initialFormData,
    loading: false,
    error: null,
    successMessage: null,
  },
  reducers: {
    resetBusinessUserState: (state) => {
      state.profile = null;
      state.appointments = [];
      state.formData = initialFormData;
      state.loading = false;
      state.error = null;
      state.successMessage = null;
    },
    setBusinessUserField: (state, action) => {
      const { field, value } = action.payload;
      state.formData[field] = value;
    },
    clearBusinessUserMessages: (state) => {
      state.successMessage = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Dashboard
      .addCase(fetchBusinessUserDashboard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBusinessUserDashboard.fulfilled, (state, action) => {
        const user = action.payload.businessUser || {};
        state.loading = false;
        state.profile = user;
        state.appointments = action.payload.appointments || [];

        // Pre-fill formData
        state.formData = {
          fullName: user.fullName || '',
          email: user.email || '',
          phone: user.phone || '',
          username: user.username || '',
          description: user.description || '',
          profession: user.profession || '',
          dob: user.dob ? user.dob.split('T')[0] : '',
          gender: user.gender || '',
          village: user.village || '',
          city: user.city || '',
          district: user.district || '',
          state: user.state || '',
          country: user.country || '',
          postalCode: user.postalCode || '',
          googleMapLink: user.googleMapLink || '',
          categoryPath: Array.isArray(user.categoryPath)
            ? user.categoryPath.join(', ')
            : '',
          profilePic: null,
          profilePicUrl: user.profilePic || '', // Store profilePic URL for display
        };
      })
      .addCase(fetchBusinessUserDashboard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to load dashboard';
      })

      // Update Profile
      .addCase(updateBusinessUserProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(updateBusinessUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload.user;
        state.successMessage = action.payload.message || 'Profile updated successfully';
      })
      .addCase(updateBusinessUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = typeof action.payload === 'string'
          ? action.payload
          : (action.payload.message || 'Failed to update profile');
      });
  },
});

export const { resetBusinessUserState, setBusinessUserField, clearBusinessUserMessages } = businessUserSlice.actions;
export default businessUserSlice.reducer;
